import { AlignHorizontalLeft, ContactMail, ContentPaste, DocumentScanner, Folder, Gavel, GroupAdd, ImportContacts, ListAlt, ReceiptLong } from "@mui/icons-material";
import moment from "moment";
import Moment from "moment-timezone";
import { Attachment, paperSizes } from "pdf-generator";
import { CorrespondanceValue, QuoteCustomItemDetail, QuoteSliceInterface, UserFields } from "../../redux/slices/quote";
import { AppRoutes } from "../../router/routes";
import { CapitalizeString, TAG_CANONICAL_NAMES, UUIDv4 } from "../../utils";
import { orientationOptions } from "../documentTemplates/dashboard/addEditTemplate";
import copy from "fast-copy";

export const mandateKeys = ["name", "currency", "creationDate", "validTill", "revision_info"];

export const quoteTextEditorButtonList = [
    ["undo", "redo"],
    ["formatBlock"],
    ["bold", "underline", "italic", "strike", "subscript", "superscript"],
    ["removeFormat"],
    ["fontColor", "hiliteColor"],
    ["outdent", "indent"],
    ["align", "horizontalRule", "list", "table"],
    ["link"],
    ["fullScreen"]
];

export const NavBars = (id: string, isEdit: boolean) => ([
    {
        id: "1",
        title: "Basic Details",
        icon: <AlignHorizontalLeft fontSize="small" />,
        path: AppRoutes.viewQuickQuoteBasicDetail(id)
    },
    {
        id: "2",
        title: "Item Details",
        disabled: !isEdit,
        icon: <ListAlt fontSize="small" />,
        path: AppRoutes.viewQuickQuoteItemDetails(id)
    },
    {
        id: "3",
        title: "Correspondence",
        disabled: !isEdit,
        icon: <ContactMail fontSize="small" />,
        path: AppRoutes.viewQuickQuoteCorrespondance(id)
    },
    {
        id: "4",
        title: "Cover Page",
        disabled: !isEdit,
        icon: <ContentPaste fontSize="small" />,
        path: AppRoutes.viewQuickQuoteCoverPage(id)
    },
    {
        id: "5",
        title: "Cover Letter",
        disabled: !isEdit,
        icon: <ImportContacts fontSize="small" />,
        path: AppRoutes.viewQuickQuoteCoverLetter(id)
    },
    {
        id: "6",
        title: "Contents Section",
        disabled: !isEdit,
        icon: <ListAlt fontSize="small" />,
        path: AppRoutes.viewQuickQuoteContentsSection(id)
    },
    {
        id: "7",
        title: "Terms & Conditions",
        disabled: !isEdit,
        icon: <Gavel fontSize="small" />,
        path: AppRoutes.viewQuickQuoteTandC(id)
    },
    {
        id: "8",
        title: "Quote Preview",
        disabled: !isEdit,
        icon: <ReceiptLong fontSize="small" />,
        path: AppRoutes.viewQuickQuotePreview(id)
    },
    {
        id: "9",
        title: "Documents",
        disabled: !isEdit,
        icon: <Folder fontSize="small" />,
        path: AppRoutes.viewQuickQuoteDocuments(id)
    },
    {
        id: "10",
        title: "Report Generation",
        disabled: !isEdit,
        icon: <DocumentScanner fontSize="small" />,
        path: AppRoutes.viewQuickQuoteReportGeneration(id)
    },
    {
        id: "11",
        title: "Manage Users",
        disabled: !isEdit,
        icon: <GroupAdd fontSize="small" />,
        path: AppRoutes.manageUsersOnQuote(id)
    },
]);

const giveMeAddressValue = (address: CorrespondanceValue) => {
    return {
        name: address.address?.name?.value,
        email: address.address?.emailId?.value,
        telephone: address.address?.phoneNumber?.value,
        street_address: address.address?.address?.value,
    }
};

const giveMeAddressConfig = (address: CorrespondanceValue) => {
    return {
        name: address.address.name.include,
        email: address.address.emailId.include,
        telephone: address.address.phoneNumber.include,
        street_address: address.address.address.include,
        enabled: address.include
    }
};

export const giveMeFromAddress = (state: QuoteSliceInterface) => {
    if (state.cover_letter.fromAddressAs) {
        let address = state.correspondance[state.cover_letter.fromAddressAs].address;
        return `${address.name.value ?? ""}\n${address.address.value ?? ""}\n${address.phoneNumber.include ? `Cell: ${address.phoneNumber.value ?? "-"}\n` : ``}${address.emailId.include ? `Email:${address.emailId.value ?? "-"}` : ``}`
    } else {
        return state.cover_letter.fromAddress
    }
};

const mapUserFields = (user: UserFields["value"]) => {
    return {
        user_email: user?.user_email || user?.email, 
        user_id: user?.user_id,
        user_name: user?.label,
    }
};

export const preparePayload = (state: QuoteSliceInterface) => {

    const tags_info = [];

    if (state.status) {
        tags_info.push({
            tag_group_id: state.status.tag_group_id,
            tag_id: state.status.value
        })
    }

    if (state.priority) {
        tags_info.push({
            tag_group_id: state.priority.tag_group_id,
            tag_id: state.priority.value
        })
    }

    if (state.tags) {
        state.tags.forEach(tag => {
            tags_info.push({
                tag_group_id: tag.tag_group_id,
                tag_id: tag.value
            })
        })
    }

    let items: any[] = [];
    let custom_items: any[] = [];

    if (state.customQuote) {
        custom_items = state.quote_custom_item_details.map((item) => {
            return {
                part_number: item.part_number,
                material_id: item?.material_id?.value,
                description: item.description,
                discount_percentage: parseFloat(`${item.discount || 0}`),
                lead_time: `${item.leadTime}`,
                quantity: parseInt(`${item.quantity || 1}`),
                unit_cost: parseInt(`${item.unitCost || 0}`)
            }
        });
    } else {
        items = state.quote_item_details.map((item) => {
            return {
                quantity: parseInt(`${item.quantity || 1}`),
                discount_percentage: parseFloat(`${item.discount || 0}`),
                lead_time: `${item.leadTime ?? ""}`,
                object_id: item.entity?.value,
                object_type: item.entityType?.value,
                bom_cost_id: (item.pricing as any)?.costing_id
            }
        });
    }

    const version_info = state.revision_info;

    return {
        name: state.name,
        serial_num: state.serial_num,
        currency_code: state.currency?.value,
        date_of_issue: state.creationDate,
        valid_till: state.validTill,
        tags_info,
        custom_quote: state.customQuote,
        items,
        custom_items,
        taxes: state.tax_details.map((item) => {
            return {
                description: item.description,
                value: parseFloat(`${item.value || 0}`)
            }
        }),
        buyer: {
            bill_to: giveMeAddressValue(state.correspondance.billTo),
            ship_to: giveMeAddressValue(state.correspondance.shipTo),
            sold_to: giveMeAddressValue(state.correspondance.soldTo),
        },
        seller: {
            bill_from: giveMeAddressValue(state.correspondance.billFrom),
            ship_from: giveMeAddressValue(state.correspondance.shipFrom),
            sold_by: giveMeAddressValue(state.correspondance.soldBy),
        },
        report_config: {
            buyer_config: {
                bill_to: giveMeAddressConfig(state.correspondance.billTo),
                ship_to: giveMeAddressConfig(state.correspondance.shipTo),
                sold_to: giveMeAddressConfig(state.correspondance.soldTo),
            },
            seller_config: {
                bill_from: giveMeAddressConfig(state.correspondance.billFrom),
                ship_from: giveMeAddressConfig(state.correspondance.shipFrom),
                sold_by: giveMeAddressConfig(state.correspondance.soldBy)
            },
            paper_size: state?.paper_size?.label,
            orientation: state?.orientation?.value,
            hf_template_id: state?.hfTemplate?.value,
            include_cover_letter_from: state?.include_cover_letter_from || false,
            include_cover_letter_subject: state?.include_cover_letter_subject || false,
            include_serial_num: state?.report_config?.include_serial_num || false,
            include_revision_table: state?.report_config?.include_revision_table || false,
            version_config: {
                include_approver: version_info?.approved_by?.include || false,
                include_creator: version_info?.created_by?.include || false,
                include_revisor: version_info?.referred_by?.include || false,
            },
        },
        cover_letter_config: {
            content_notes: state.cover_letter.content,
            designation: state.cover_letter.designation,
            from_address: state.cover_letter.fromAddressAs ? giveMeFromAddress(state) : state.cover_letter.fromAddress,
            is_active: state.cover_letter.include,
            name: state.cover_letter.name,
            regards: state.cover_letter.regards,
            subject: state.cover_letter.subjectAsName ? state.name : state.cover_letter.subject,
            signature_image: {
                attachment_id: state?.cover_letter?.attachments?.[0]?._id
            }
        },
        notes: state.terms_and_conditions.length > 0 ? state.terms_and_conditions.filter(tAndC => Object.keys(tAndC).length > 0 && tAndC?.heading?.trim() && tAndC?.notes?.trim()).map((tAndC) => ({
            content: tAndC.notes?.trim(),
            title: tAndC.heading?.trim()
        })) : [],
        contents: state.contents.length > 0 ? state.contents.filter(content => Object.keys(content).length > 0 && content?.heading?.trim() && content?.notes?.trim()).map((content) => ({
            content: content.notes?.trim(),
            title: content.heading?.trim()
        })) : [],
        cover_page_config: {
            heading: state.cover_page_config?.heading ?? "",
            include_cover_page: state.cover_page_config?.include_cover_page || false,
            include_heading: true,
            // include_heading: state.cover_page_config?.include_heading || false,
            image: {
                position: {
                    x: (+state.cover_page_config.logo?.horizontal) || 0,
                    y: (+state.cover_page_config.logo?.vertical) || 0,
                },
                height: state.cover_page_config.logo.height || 0,
                width: state.cover_page_config.logo.width || 0,
                attachment_id: state.cover_page_config.attachments?.[0]?._id
            }
        },
        quote_report: {
            name: state.name,
            orientation: state.hfTemplate?.orientation || state.orientation?.value || orientationOptions[0].value,
            paper_size: state.hfTemplate?.paper_size || state.paper_size?.value || paperSizes[4].value,
            use_mb_formatter: state.quote_report?.use_mb_formatter ?? false,
            table_header_color: state.quote_report?.table_header_color ?? "#d1c4e9",
            footer_url: state.quote_report?.footer_url ?? `${window.location.origin}${AppRoutes.viewQuickQuoteBasicDetail(state.id)}`,
            show_footer: state.quote_report?.show_footer ?? false,
            template_id: state?.hfTemplate?.value,
            include_serial_no: state.report_config.include_serial_num || false,
            template_type: 'hfTemplate',
        },
        version_info: {
            description: version_info?.description ?? "",
            approver: version_info?.approved_by?.value ? mapUserFields(version_info?.approved_by?.value) : undefined,
            creator: version_info?.created_by?.value ? mapUserFields(version_info?.created_by?.value) : undefined,
            revisor: version_info?.referred_by?.value ? mapUserFields(version_info?.referred_by?.value) : undefined,
            date_time: version_info?.date ? new Date(version_info?.date).toISOString() : undefined, 
        },
        time_zone: state.time_zone ?? Moment.tz.guess()
    }
};

export const buildObjectForState = (_quoteData: any): QuoteSliceInterface => {
    const quoteData = copy(_quoteData);

    const report_config = quoteData.data?.report_config;

    const getCorrespondanceValue = (configKey: any, parentKey: any, addressKey: any) => {
        return {
            include: report_config?.[configKey]?.[addressKey]?.enabled ?? false,
            address: {
                name: {
                    include: report_config?.[configKey]?.[addressKey]?.name ?? false,
                    value: quoteData.data[parentKey]?.[addressKey]?.name ?? ""
                },
                emailId: {
                    include: report_config?.[configKey]?.[addressKey]?.email ?? false,
                    value: quoteData.data[parentKey]?.[addressKey]?.email ?? ""
                },
                phoneNumber: {
                    include: report_config?.[configKey]?.[addressKey]?.telephone ?? false,
                    value: quoteData.data[parentKey]?.[addressKey]?.telephone ?? ""
                },
                address: {
                    include: report_config?.[configKey]?.[addressKey]?.street_address ?? false,
                    value: quoteData.data[parentKey]?.[addressKey]?.street_address ?? ""
                },
            }
        }
    };

    const cover_page_config = quoteData.data?.cover_page_config;
    const cover_page_image_details = cover_page_config?.image;
    const cover_page_image = quoteData.data?.attachments?.find((_: Attachment) => _?._id === cover_page_image_details?.attachment_id);
    const quote_report_attachment = quoteData.data?.attachments?.find((_: Attachment) => _?._id === quoteData.data?.quote_report?.attachment_id);

    if (quote_report_attachment) {
        quoteData.data.quote_report.status = quote_report_attachment.status;
    }

    const version_info = quoteData.data?.version_info;

    return {
        id: quoteData.data._id,
        name: quoteData.data.name,
        version: quoteData.data.version,
        released: quoteData.data.released,
        sequence_id: quoteData?.data?.sequence_id ?? "New",
        serial_num: quoteData?.data?.serial_num ?? undefined,
        customQuote: quoteData?.data?.custom_quote ?? false,
        status: quoteData?.data?.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.quote_status)?.map((_: any) => ({ ..._, label: _.tag_name, value: _.tag_id }))?.[0] ?? null,
        priority: quoteData?.data?.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.quote_priority)?.map((_: any) => ({ ..._, label: _.tag_name, value: _.tag_id }))?.[0] ?? null,
        currency: {
            label: quoteData.data.currency_code,
            value: quoteData.data.currency_code
        },
        tags: quoteData?.data?.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.quote_tg)?.map((_: any) => ({ ..._, label: _.tag_name, value: _.tag_id })) ?? [],
        hfTemplate: report_config?.hf_template_details ? {
            ...report_config?.hf_template_details,
            value: report_config?.hf_template_id,
            label: `${report_config?.hf_template_details?.sequence_id} - ${report_config?.hf_template_details?.name}`
        } : null,
        creationDate: moment(quoteData.data.date_of_issue),
        validTill: moment(quoteData.data.valid_till),
        quote_item_details: quoteData.data.items?.map((item: any) => ({
            id: UUIDv4(),
            entityType: {
                value: item.object_type,
                label: CapitalizeString(item.object_type)
            },
            entity: item.object_id ? { value: item.object_id, label: `${item.object_sequence_id} (${item.object_name})` } : null,
            pricing: item.pricing_info ? {
                ...item.pricing_info,
                total_cost: item.unit_cost,
                costing_id: item.bom_cost_id,
                value: item.pricing_info._id,
                label: `${item.pricing_info.sequence_id} (${item.pricing_info.pricing_name})`,
                isCalculated: typeof item.unit_cost === "number" ? true : false
            } : null,
            partNumber: `${item.pricing_info.bom_cost?.sequence_id ?? ""}-r${item.pricing_info.bom_cost?.version ?? ""}`,
            description: item.pricing_info.bom_cost?.short_description ?? "",
            quantity: item.quantity,
            discount: item.discount_percentage,
            leadTime: item.lead_time,
            totalCost: item.total_cost,
            unitCost: item.unit_cost
        })) ?? [],
        quote_custom_item_details: quoteData.data.custom_items?.map((item: any) => {
            let payload: QuoteCustomItemDetail = {
                id: UUIDv4(),
                material_id: item?.material_id ? { value: item?.material_id, label: item.part_number, _id: item?.material_id } : null,
                part_number: item?.part_number,
                description: item.description ?? "",
                unitCost: item.unit_cost ?? 0,
                quantity: item.quantity ?? 1,
                discount: item.discount_percentage ?? 0,
                leadTime: item.lead_time ?? "",
                totalCost: 0
            }

            payload.totalCost = payload.quantity * payload.unitCost;
            payload.totalCost = payload.totalCost - ((payload.discount / 100) * payload.totalCost);

            return payload;
        }),
        tax_details: quoteData.data.taxes?.map((tax: any) => ({ id: UUIDv4(), ...tax })) ?? [],
        total_quote_price: quoteData.data.final_quote_price ?? 0,
        total_quote_item_price: quoteData.data.total_quote_price ?? 0,
        total_quote_tax_price: quoteData.data.total_tax ?? 0,
        correspondance: {
            soldBy: getCorrespondanceValue('seller_config', 'seller', 'sold_by'),
            soldTo: getCorrespondanceValue('buyer_config', 'buyer', 'sold_to'),
            billFrom: getCorrespondanceValue('seller_config', 'seller', 'bill_from'),
            billTo: getCorrespondanceValue('buyer_config', 'buyer', 'bill_to'),
            shipFrom: getCorrespondanceValue('seller_config', 'seller', 'ship_from'),
            shipTo: getCorrespondanceValue('buyer_config', 'buyer', 'ship_to'),
        },
        cover_letter: {
            include: quoteData.data?.cover_letter_config?.is_active ?? false,
            fromAddress: quoteData.data?.cover_letter_config?.from_address ?? "",
            fromAddressAs: undefined,
            subject: quoteData.data?.cover_letter_config?.subject ?? "",
            subjectAsName: false,
            content: quoteData.data?.cover_letter_config?.content_notes ?? "",
            regards: quoteData.data?.cover_letter_config?.regards ?? "",
            name: quoteData.data?.cover_letter_config?.name ?? "",
            designation: quoteData.data?.cover_letter_config?.designation ?? "",
            attachments: quoteData.data.attachments?.filter((_: Attachment) => _?._id === quoteData.data?.cover_letter_config?.signature_image?.attachment_id) ?? []
        },
        terms_and_conditions: quoteData.data?.notes?.map((note: any) => ({
            id: UUIDv4(), heading: note.title, notes: note.content
        })) ?? [],
        contents: quoteData.data?.contents?.map((note: any) => ({
            id: UUIDv4(), heading: note.title, notes: note.content
        })) ?? [],
        paper_size: {
            label: report_config?.paper_size ?? "A4",
            value: report_config?.paper_size ?? "A4",
        },
        orientation: {
            label: CapitalizeString(report_config?.orientation ?? "Portrait"),
            value: report_config?.orientation ?? "portrait",
        },
        include_cover_letter_from: report_config?.include_cover_letter_from || false,
        include_cover_letter_subject: report_config?.include_cover_letter_subject || false,
        report_config: {
            include_serial_num: report_config?.include_serial_num || false,
            include_revision_table: report_config?.include_revision_table || false,
        },
        cover_page_config: {
            logo: {
                isActive: true,
                // isActive: cover_page_image ? true : false,
                src: null,
                vertical: cover_page_image_details?.position?.y ?? 0,
                horizontal: cover_page_image_details?.position?.x ?? 0,
                width: cover_page_image_details?.width ?? 0,
                height: cover_page_image_details?.height ?? 0,
            },
            include_cover_page: cover_page_config?.include_cover_page || false,
            include_heading: true,
            // include_heading: cover_page_config?.include_heading || false,
            heading: cover_page_config?.heading ?? "",
            attachments: cover_page_image ? [cover_page_image] : []
        },
        quote_report: {
            ...quoteData.data?.quote_report,
            include_serial_no: report_config?.include_serial_num ?? false
        },
        revision_info: {
            description: version_info?.description ?? "",
            approved_by: {
                include: report_config?.version_config?.include_approver ? true : false,
                value: version_info?.approver ? { ...version_info?.approver, label: version_info?.approver?.user_name, value:  version_info?.approver?.user_id } : null,
            },
            created_by: {
                include: report_config?.version_config?.include_creator ? true : false,
                value: version_info?.creator ? { ...version_info?.creator, label: version_info?.creator?.user_name, value:  version_info?.creator?.user_id } : null,
            },
            referred_by: {
                include: report_config?.version_config?.include_revisor ? true : false,
                value: version_info?.revisor ? { ...version_info?.revisor, label: version_info?.revisor?.user_name, value:  version_info?.revisor?.user_id } : null,
            },
            date: version_info?.date_time ?? "",
            history: quoteData.data?.version_history ?? []
        },
        time_zone: quoteData.data?.time_zone ?? Moment.tz.guess(),
        fetched_at: new Date().toISOString(),
        attachments: quoteData.data.attachments || []
    }
}

export const SECTION_LIMIT = 10;

export const HEADING_LIMIT = 200;

export const NOTE_LIMIT = 5000;